import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { computeSegments } from "@smsbridge/shared";
import type { ConversationMessage, CursorPage } from "@smsbridge/shared";
import { Button } from "../../components/ui/Button";
import { Skeleton } from "../../components/ui/Skeleton";
import { StatusPill } from "../../components/ui/StatusPill";
import { api, apiErrorMessage } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { toast } from "../../lib/toast";

const PAGE_SIZE = 30;

export interface ConversationThreadProps {
  /** The counterparty. A phone number in any format, an alphanumeric sender ID, or a short code. */
  peer: string;
  /** Height of the scrolling message area. The dialog and the full page want different heights. */
  className?: string;
}

/**
 * The conversation itself: history, chat bubbles, and a reply box.
 *
 * Deliberately a standalone component with `peer` as its only real input, because there are two
 * entry points into the same feature — the "View conversation" dialog on the Sent/Received tables,
 * and (later) a dedicated Conversations page. Both mount THIS component, so they cannot drift into
 * two implementations that show subtly different things.
 *
 * Live updates come from the app's existing SSE feed. This component does not open a stream of its
 * own: `useGatewayEvents` (already mounted by MessagingPage) invalidates the `["conversations"]`
 * query key when a message event arrives, which is enough to refetch an open thread. One
 * EventSource for the whole app, not one per dialog.
 */
export function ConversationThread({ peer, className }: ConversationThreadProps) {
  const queryClient = useQueryClient();
  const [pages, setPages] = useState(1);
  const [body, setBody] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // The API returns newest-first because a chat opens at the bottom and pages backwards. Asking
  // for `pages * PAGE_SIZE` keeps "load older" simple: one query key, one refetch, and a live
  // update never drops the older messages the user already pulled in.
  const limit = pages * PAGE_SIZE;

  const threadQuery = useQuery({
    queryKey: ["conversations", peer, "thread", limit],
    queryFn: async () =>
      (
        await api.get<CursorPage<ConversationMessage>>(
          `/gateway/conversations/${encodeURIComponent(peer)}`,
          { params: { limit } },
        )
      ).data,
  });

  /**
   * Opening a conversation is what marks it read — same gesture as every messaging app.
   *
   * Fired once per peer on mount. The endpoint is idempotent (a second call marks zero), so there
   * is no need to guard against a re-render racing it.
   */
  useEffect(() => {
    void api
      .patch(`/gateway/conversations/${encodeURIComponent(peer)}/read`)
      .then(() => {
        // Refresh the list's unread badge, but not this thread — refetching here would fight the
        // scroll position for no visible gain.
        void queryClient.invalidateQueries({ queryKey: ["conversations", "list"] });
      })
      .catch(() => {
        // A failed read-receipt is not worth a toast: the messages are on screen either way, and
        // the next open will retry.
      });
  }, [peer, queryClient]);

  const { segments } = useMemo(() => computeSegments(body || " "), [body]);

  const sendMutation = useMutation({
    mutationFn: async () =>
      // The exact endpoint and payload shape ComposeForm uses. Not a parallel send path: same
      // validation server-side, same delivery pipeline, same status tracking.
      (await api.post("/gateway/send-sms", { recipients: [peer], message: body.trim() })).data,
    onSuccess: () => {
      setBody("");
      toast.success("Message queued.");
      // Both keys: the thread gains a bubble, and the list's preview and ordering change.
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
      void queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not send.")),
  });

  const items = threadQuery.data?.items ?? [];
  // Oldest at top, which is the direction a conversation reads.
  const ordered = useMemo(() => [...items].reverse(), [items]);

  // Pin to the newest message whenever the set of messages changes, so a reply that arrives over
  // SSE is actually visible rather than sitting below the fold.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ordered.length]);

  const canSend = body.trim().length > 0 && !sendMutation.isPending;

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div
        ref={scrollRef}
        className={`scrollbar-slim flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain pr-1 ${
          className ?? "h-[min(58vh,26rem)]"
        }`}
      >
        {threadQuery.isLoading ? (
          <ThreadSkeleton />
        ) : threadQuery.isError ? (
          <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {apiErrorMessage(threadQuery.error, "Could not load this conversation.")}
          </p>
        ) : ordered.length === 0 ? (
          <p className="py-8 text-center text-sm text-text-muted">
            No messages with this contact yet.
          </p>
        ) : (
          <>
            {threadQuery.data?.hasMore && (
              <div className="flex justify-center pb-1">
                <Button
                  variant="secondary"
                  onClick={() => setPages((p) => p + 1)}
                  loading={threadQuery.isFetching}
                >
                  Load older messages
                </Button>
              </div>
            )}
            {ordered.map((m) => (
              <Bubble key={m._id} message={m} />
            ))}
          </>
        )}
      </div>

      <form
        className="flex items-end gap-2 border-t border-border pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSend) sendMutation.mutate();
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Reply to ${peer}`}
            aria-label={`Reply to ${peer}`}
            rows={2}
            // min-h because `rows` is only an initial height: a resizable textarea can be dragged
            // shut, and a collapsed compose box looks broken.
            className="min-h-[3.5rem] w-full resize-y rounded-control border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter is a newline — the convention in every chat client.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) sendMutation.mutate();
              }
            }}
          />
          <span className="text-xs text-text-muted">
            {body.trim() ? `${segments} segment${segments === 1 ? "" : "s"}` : "Enter to send · Shift+Enter for a new line"}
          </span>
        </div>
        <Button
          onClick={() => canSend && sendMutation.mutate()}
          disabled={!canSend}
          loading={sendMutation.isPending}
          leftIcon={<Send className="size-4" aria-hidden />}
        >
          Send
        </Button>
      </form>
    </div>
  );
}

/** One message. Outbound sits right in the accent colour, inbound left in a neutral surface. */
function Bubble({ message }: { message: ConversationMessage }) {
  const outbound = message.direction === "SENT";
  return (
    <div className={`flex ${outbound ? "justify-end" : "justify-start"}`}>
      <div className={`flex max-w-[85%] flex-col gap-1 sm:max-w-[75%] ${outbound ? "items-end" : "items-start"}`}>
        <div
          className={`whitespace-pre-wrap break-words rounded-card px-3 py-2 text-sm ${
            outbound
              ? "bg-accent text-white"
              : "border border-border bg-surface text-text"
          }`}
        >
          {message.body}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-text-muted">{formatDateTime(message.createdAt)}</span>
          {/* Status only means something outbound; an inbound message is simply here. */}
          {outbound && <StatusPill status={message.status} />}
        </div>
      </div>
    </div>
  );
}

/** Alternating bubble shapes, so the load reads as a conversation rather than a generic block. */
function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-3 py-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
          <Skeleton
            variant="card"
            width={i % 3 === 0 ? "58%" : i % 3 === 1 ? "42%" : "70%"}
            height="2.75rem"
          />
        </div>
      ))}
    </div>
  );
}
